define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"src/c8-platform/core/request",
	"csi/reflection",
	"csi/utility/dateUtility",
	"csi/utility/stringUtility",
	"csi/xml"
], function(declare, lang, {errorIsUnhandledException}, reflection, {convertCentricToStandardLocale}, {stringSubstitute}, xml) {

	var ACTION_NAME_SUFFIX = "_Name",
		DESC1 = "${0}: ${1}",
		DESC2 = "${0} ${1}: ${2}",
		DESC3 = "${0}: ${1}, ${2}: ${3}",
		DESC_PREFIX = "Locale",
		DESC_TYPE = "LocaleType",
		DESC_RESOURCE = "LocaleResource",
		SUB_RE = /\$\{([^\s\:\}]*)(?:\:([^\s\:\}]+))?\}/g,
		SUB_TYPE_CURRENT = "nlsTypeCurrent",
		TOGGLE = "_TG",
		TYPE_VIEW = "View";

	// function list used to format the localization
	const MAP_FORMATS = {
		"_lower": function(s) {
			return s.toLowerCase();
		},

		"_upper": function(s) {
			return s.toUpperCase();
		}
	};

	// DEBUG - used to analyze localization in various ways
	var _tracking = null;

	return declare(null, {
		// Parameterization:
		//		Global resources, Attributes, and View resources support parameterization, which means they can reference
		//		other global resources or type resources. References are not recursive, so referencing a resource that is
		//		itself parameterized will not work.
		//
		// 		Example: Reference a global resource using simple string
		// 			"Sample Supplier": "${PhaseSample} Supplier"
		//
		// 		Example: Reference a global resource using simple string
		// 			"Sample Supplier": "${PhaseSample} Supplier"
		//
		// 		Example: Reference a type resource using "." separated string
		// 			"Sample Supplier": "Sample ${Supplier.Name}"
		//
		// 		Example: Special case - reference type in current context (used for tab names of current node)
		// 			"__Base__": "nlsTypeCurrent"

		// constants
		statics: {
			ATTR_VALIDATION: "_IV",
			TOOLTIP: "_TT",
			TYPE_ACTION: "Action",
			TYPE_ATTR: "Attr",
			TYPE_PATH: "Path",
			TYPE_PLURAL: "Plural"
		},

		// Boolean: indicates that in configuration mode
		bConfigure: false,

		// Boolean: indicates if resource parameters should be substituted with localized strings or returned as-is
		bSubstitute: true,

		// Boolean: indicates if default names should be used if resource does not exist
		bUseDefault: true,

		// String: view id of current view
		idView: null,

		// LocaleResources: resources used to resolve names
		localeResources: null,

		// Object: object containing view-specific localizations
		mapView: null,

		// LocaleResolver: optional resolver used to first resolve parameters
		paramResolver: null,

		// Class: reflection class used to resolve last key - used in localization UI to know at which class a key was localized
		rcLastType: null,

		// String: node type in context
		typeContext: null,

		// String: node type on which current view is defined
		typeView: null,

		// String: string used to compose attribute names with path name
		_pathNamePattern: null,

		constructor: function(/*LocaleResources*/res, /*Object?*/params) {
			this.localeResources = res;

			if (params) {
				lang.mixin(this, params);
			}

			if (this.typeView) {
				this.mapView = res.view(this.typeView, this.idView);
			}

			this._pathNamePattern = res.resource("PathNamePattern");
		},

		/**
		 * Capitalize the pure text based on the current locale
		 * @param {string} text
		 * @return {string}
		 */
		capitalizeText: function(text) {
			return text.toLocaleUpperCase(this.getLocale(true));
		},

		getAttributeType: function(/*String*/t) {
			// summary: return localized name for attribute value type
			return this.getResourceIfExists("AttributeType_" + t) || t;
		},

		getEnumName: function(/*String*/enumValue, /*Boolean?*/capitalize) {
			// summary: return localized name for enumValue
			if (!enumValue) {
				return null;
			}

			let s = this.getResourceIfExists(enumValue);
			if (s == null) {
				var v = xml.enumGetText(enumValue);
				if (v) {
					// only OOTB enums can reference types, so okay to rely on reflection cache
					var enumList = reflection.getEnumList(xml.enumGetId(enumValue));
					if (enumList?.containsClassNames) {
						// return null if class does not exist or is disabled (abstract)
						var rc = reflection.getClass(v);
						if (!rc?.enabled) {
							return null;
						}

						s = this.getTypeName(v);
					}
				}

				if (!s && this.bUseDefault) {
					s = v;
				}
			}

			return capitalize ? this.capitalizeText(s) : s;
		},

		/**
		 * Return localized message for given error code and params
		 * @param {Error} error 
		 * @return {string}
		 */
		getErrorMessage(error) {
			const {csiErrorCode, csiErrorParams} = error;
			return csiErrorCode ? this.getErrorMessageForCode(csiErrorCode, csiErrorParams) : (error.message || "");
		},

		/**
		 * Return localized message for given error code and params
		 * @param {string} errorCode
		 * @param {Array<string | number>} [params]
		 * @return {string}
		 */
		getErrorMessageForCode(errorCode, params) {
			let message = "";

			if (!errorIsUnhandledException(errorCode)) {
				errorCode?.split(';').forEach((code) => {
					const s = this.resolveResource(code, params);
					if (s) {
						message += (message ? "\n" : "") + s;
					}
				});
			}

			return message || this.getResource("REQUEST_ERROR");
		},

		getLocale: function(/*Boolean*/rfc3066Format) {
			// summary: return current locale in C8 format (with underscore) or RFC3066 format (with dash)
			// See http://www.ietf.org/rfc/rfc3066.txt for RFC3066 description.
			const {locale} = this.localeResources;
			return rfc3066Format ? convertCentricToStandardLocale(locale) : locale;
		},

		getParameterDescription: function(/*String*/param) {
			// summary: access resources or types to get localized string for JSON reference (A.B.C)
			var a = param.split(".");

			if (a.length == 1) {
				return this._stringSubstitute(DESC1, [this.getResource(DESC_RESOURCE), a[0]]);
			} else if (a.length == 2) {
				return this._stringSubstitute(DESC2, [this.getResource(DESC_TYPE), this.getResource(DESC_PREFIX + a[1]), a[0]]);
			}

			return this._stringSubstitute(DESC3, [this.getResource(DESC_TYPE), a[0], this.getResource(DESC_PREFIX + a[1]), a[2]]);
		},

		getParameterResource: function(/*String*/param) {
			// summary: access resources or types to get localized string for JSON reference (A.B.C)
			return (param.indexOf(".") > 0) ? lang.getObject(param, false, this.localeResources.types) : this.localeResources.resource(param);
		},

		getResource: function(/*String*/idRes, /*String[]?*/params) {
			// summary: convert resource to displayable string - return original if no mapping
			var s = this.getResourceIfExists(idRes, params);
			return (s != null) ? s : (this.bUseDefault ? idRes : null);
		},

		getResourceIfExists: function(/*String*/idRes, /*String[]?*/params) {
			// summary: only return mapped string if it exists - do not default to resource key
			var s = this.localeResources.resource(idRes);
			return s ? this._stringSubstitute(s, params) : s;
		},

		getResourceTooltip: function(/*String*/idRes, /*String[]*/params) {
			// summary: get tooltip for resource
			return this.getResourceIfExists(idRes + this.statics.TOOLTIP, params);
		},

		getTypeAction: function(/*String*/idType, /*String*/idAction, /*String[]?*/params) {
			// summary: convert action id (including possible suffix) to displayable string
			return this._getTypeObjectResource(idType, this.statics.TYPE_ACTION, idAction, params);
		},

		getTypeAttribute: function(/*String*/idType, /*String*/idAttr) {
			// summary: convert attribute id to displayable string
			if (idType) {
				// check for attribute mapping for type or super class of type
				var s = this._getTypeObjectResource(idType, this.statics.TYPE_ATTR, idAttr);
				if ((s != null) || !this.bUseDefault) return s;

				// if Node Name attribute, then default to type name
				if (idAttr == xml.ATTR_NAME) {
					if (this.paramResolver) {
						s = this.paramResolver.getTypeName(idType);
					}

					return s || this.getTypeName(idType);
				}
			}

			// look in resources as last recourse - this is done primarily for built-in attributes such as "Node Type" and "__Parent__"
			var s = this.getResourceIfExists(idAttr);
			if (s != null) {
				return s;
			}

			return (this.bUseDefault && this.bSubstitute) ? idAttr : null;
		},

		getTypeAttributeAlongPath: function(/*String*/type, /*String*/idAttr, /*String?*/pathName) {
			// summary: convert attribute id and path name to displayable string
			var name = this.getTypeAttribute(type, idAttr);
			return pathName ? stringSubstitute(this._pathNamePattern, {Attr: name, Path: pathName}) : name;
		},

		getTypeAttributeKey: function(/*String*/idType, /*String*/idAttr, /*String*/suffix) {
			// summary: return localized tooltip for attribute
			return [idType, this.statics.TYPE_ATTR, idAttr + suffix].join(".");
		},

		getTypeAttributeResource: function(/*String*/idType, /*String*/idAttr, /*String?*/suffix) {
			// summary: return attribute resource
			return this._getTypeObjectResource(idType, this.statics.TYPE_ATTR, idAttr + (suffix || ""));
		},

		getTypeName: function(/*String*/idType, /*Boolean?*/lowercase) {
			// summary: return name for type
			const s = this.localeResources.typeName(idType) || (this.bUseDefault ? idType : null);
			return (s && lowercase) ? this.lowercaseText(s) : s;
		},

		getTypePath: function(/*String*/idType, /*String*/path) {
			// summary: return localized string for path
			return this._getTypeObjectResource(idType, this.statics.TYPE_PATH, path);
		},

		getTypePlural: function(/*String*/idType, /*Boolean?*/lowercase) {
			// summary: return localized string for path
			const s = this.localeResources.typeResource(idType, this.statics.TYPE_PLURAL);
			return (s && lowercase) ? this.lowercaseText(s) : s;
		},

		getTypeResource: function(/*String*/idType, /*String*/idRes) {
			// summary: get resource directly stored on type
			var s = this.localeResources.typeResource(idType, idRes);
			return (s != null) ? this._stringSubstitute(s) : s;
		},

		getTypeView: function(/*String*/idType, /*String*/idView) {
			// summary: return localized string for view
			var s = this._getTypeObjectResource(idType, TYPE_VIEW, idView);
			return (s != null) ? s : (this.bUseDefault ? idView : null);
		},

		getTypeViewResource: function(/*String*/idType, /*String*/idView, /*String*/key) {
			// summary: return localized string for view
			var obj = this.localeResources.view(idType, idView);
			var s = obj ? obj[key] : null;
			return (s != null) ? this._stringSubstitute(s) : s;
		},

		getViewResource: function(/*String*/key) {
			// get the view mapping if it exists - make sure to support empty strings
			var s = this.mapView ? this.mapView[key] : null;
			return (s != null) ? this._stringSubstitute(s) : s;
		},

		inConfigurationMode: function() {
			// summary: indicate if map is being used to configure view localization
			return this.bConfigure;
		},

		/**
		 * Convert the given text to lowercase for the current locale
		 * @param {string} text
		 * @return {string}
		 */
		lowercaseText(text) {
			return text.toLocaleLowerCase(this.getLocale(true));
		},

		resolveActionNames: function(/*String*/keyView, /*String?*/type, /*String*/id, /*String?*/tag, /*String[]*/nameParams) {
			// summary: determine localization name for Action - name and toggle should always be configured together
			var ACT = this.statics.TYPE_ACTION;
			var TT = this.statics.TOOLTIP;

			// FIX: deprecate use of view resources for action names
			var nm = this.getViewResource(keyView);
			var tg = this.getViewResource(keyView + TOGGLE);
			var tt = this.getViewResource(keyView + TT);

			if (type) {
				var keyType = id || tag;

				if (nm == null) {
					nm = this._getTypeObjectResource(type, ACT, keyType, nameParams);
					tg = this._getTypeObjectResource(type, ACT, keyType + TOGGLE, nameParams);
				}
				tt = tt || this._getTypeObjectResource(type, ACT, keyType + TT, nameParams);
			}

			if (this.bUseDefault) {
				if (id) {
					if (nm == null) {
						nm = this.getResourceIfExists(id, nameParams);
						tg = this.getResourceIfExists(id + TOGGLE, nameParams);
					}
					tt = tt || this.getResourceIfExists(id + TT, nameParams) || this.getResourceIfExists(tag + TT, nameParams);
				}

				if (nm == null) {
					if (tag) {
						var keyTagName = tag + ACTION_NAME_SUFFIX;
						nm = this.getResourceIfExists(keyTagName, nameParams) || this.getResource(tag, nameParams);
						tg = this.getResourceIfExists(keyTagName + TOGGLE, nameParams) || this.getResourceIfExists(tag + TOGGLE, nameParams);
					} else {
						nm = id;
					}
				}
			}

			return {name: nm, nameToggle: tg, tooltip: tt};
		},

		resolveHeadingNames: function(/*String?*/keyView, /*String?*/type, /*String?*/idAttr, /*String?*/prop, /*String?*/name) {
			// summary: determine localization name for Heading
			var nm = this.getViewResource(keyView);
			var tt = this.getViewResource(keyView + this.statics.TOOLTIP);

			if (this.bUseDefault) {
				if (nm == null) {
					if (name != null) {
						// resolve globally
						nm = this.getResource(name);
					} else if (idAttr) {
						// resolve as attribute if known
						nm = type ? this.getTypeAttribute(type, idAttr) : this.getResource(idAttr);
					} else if (prop === xml.PROP_CREATE_TIME) {
						nm = this.getResource(reflection.getClass(type)?.isRevision() ? "RevisionCreated" : prop);
					} else if (prop === xml.PROP_CREATOR) {
						nm = this.getResource(reflection.getClass(type)?.isRevision() ? "RevisionCreatedBy" : prop);
					} else if (prop) {
						// resolve property - if Name then resolve similar to "Node Name" attribute, otherwise just lookup in global renaming
						nm = (type && (prop == xml.PROP_NAME)) ? this.getTypeName(type) : this.getResource(prop);
					}
				}

				if ((tt == null) && (name == null) && type && idAttr) {
					// default tooltip to that of attribute
					tt = this.getTypeAttributeResource(type, idAttr, this.statics.TOOLTIP);
				}
			}

			return {name: nm, tooltip: tt};
		},

		resolveResource: function(/*String*/key, /*String[]*/params) {
			// summary: return the message associated with given key (from resources or types) substituting all parameters
			return this._stringSubstitute(this.getParameterResource(key), params) || "";
		},

		_getTypeObjectResource: function(/*String*/idType, /*String*/idObject, /*String*/id, /*String[]?*/params) {
			// summary: look for resource in type and super classes of type
			var rcRoot = reflection.getClass(idType);
			var lr = this.localeResources;
			var s;

			if (rcRoot) {
				this.rcLastType = rcRoot.traverse(true, false, function(rc) {
					s = lr.typeObjectResource(rc.getType(), idObject, id);
					return s != null;
				});
			} else {
				// handle case where class is not in reflection model (made up on client)
				// FIX: not sure this case exists anymore, but keeping just in case
				s = lr.typeObjectResource(idType, idObject, id);
			}

			return s ? this._stringSubstitute(s, params) : s;
		},

		_stringSubstitute: function(/*String*/s, /*String[]?*/params) {
			// summary: replace string parameters with those given or those of referenced resources
			if (!s || !s.replace || !this.bSubstitute) return s;

			var self = this;

			return s.replace(SUB_RE, function(match, key, format) {
				var x;

				if (isNaN(key)) {
					// return name for current type if nlsTypeCurrent
					if (key == SUB_TYPE_CURRENT) {
						return self.getTypeName(self.typeContext);
					}

					// check other resolver first if available
					if (self.paramResolver) {
						x = self.paramResolver.getParameterResource(key);
					}

					if (x == null) {
						x = self.getParameterResource(key);
					}
				} else if (params) {
					// return key for numbered index
					x = params[key];
				}

				x = (x == null) ? match : x;

				// do the additional format as configured
				const f = MAP_FORMATS[format];

				return f ? f(x) : x;
			});
		},


		// DEBUG

		_getTracked: function(code, key, id, rc, idAttr) {
			if (!_tracking || (!key && !id && !idAttr)) return false;

			var a = _tracking[code];
			if (!a) return false;

			var k = (key || "") + ":" + (id || "") + ":" + (idAttr || "") + ":" + (rc ? rc.getType() : "");
			return a[k];
		},

		_track: function(code, key, id, rc, idAttr, x) {
			if (!_tracking || (!key && !id && !idAttr)) return;

			var a = _tracking[code];
			if (!a) {
				a = _tracking[code] = {};
			}

			var k = (key || "") + ":" + (id || "") + ":" + (idAttr || "") + ":" + (rc ? rc.getType() : "");
			if (!a[k]) {
				a[k] = [key, id, idAttr, rc, x, this.type, this.view];
			}
		}
	});
});